import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Drawer, IconButton, Tooltip } from '@material-ui/core';
import { Close, Fullscreen, FullscreenExit } from '@material-ui/icons';
import zIndex from '@material-ui/core/styles/zIndex';
import { env } from 'config';
import { ensureFreshToken, REFRESH_TOKEN_KEY } from 'keycloak';
import AIBot from 'assets/icons/svg/AIBot';

const finalUrl = env.AI_WIDGET_URL;
const widgetOrigin = new URL(finalUrl).origin;

const AIChat = () => {
  const ref = useRef<HTMLIFrameElement>(null);
  const [open, setOpen] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const sendToken = useCallback((token: string) => {
    ref.current?.contentWindow?.postMessage({ token }, finalUrl);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== widgetOrigin) return;

      if (event.data === 'token') {
        // Виджет живёт с этим токеном до конца своей сессии, поэтому отдаём заведомо свежий,
        // а не копию из localStorage, которая к этому моменту может быть на исходе
        ensureFreshToken().then((accessToken) => {
          if (accessToken) sendToken(accessToken);
        });
      }

      if (event.data === 'refresh') {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (refreshToken) {
          sendToken(refreshToken);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [sendToken]);

  useEffect(() => {
    if (!ref.current?.contentWindow) return;

    ensureFreshToken().then((accessToken) => {
      if (accessToken) sendToken(accessToken);
    });
  }, [sendToken]);

  return (
    <>
      <Box sx={{ position: 'fixed', bottom: 12, right: 12, zIndex: zIndex['modal'] }}>
        <Tooltip title="AI Ассистент">
          <IconButton size="medium" onClick={handleToggle}>
            <AIBot style={{ fontSize: 48 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        SlideProps={{
          onExited: () => {
            setIsFullscreen(false);
          },
        }}
        PaperProps={{
          style: {
            overflow: 'hidden',
            width: isFullscreen ? '100vw' : '35vw',
          },
        }}
      >
        <Box sx={{ position: 'relative', height: '100%' }}>
          <Box
            style={{
              position: 'absolute',
              top: 14,
              right: 14,
              zIndex: 1,
              display: 'flex',
              gap: 8,
            }}
          >
            <Tooltip title={isFullscreen ? 'Свернуть' : 'На весь экран'}>
              <IconButton size="small" onClick={toggleFullscreen}>
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
            {isFullscreen && (
              <Tooltip title="Закрыть">
                <IconButton size="small" onClick={handleClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <iframe ref={ref} src={finalUrl} title="AI Chat" style={{ width: '100%', height: '100%', border: 'none' }} />
        </Box>
      </Drawer>
    </>
  );
};

export default AIChat;
